import {
  PlanModelV2,
  TankPlanModel,
  UpdatePlanV2,
} from "@/api/dischargePlan/types";
import { defineStore } from "pinia";
import { computed, onMounted, ref, watch } from "vue";
import dischargePlanApi from "@/api/dischargePlan";
import receiptPlanApi from "@/api/receiptPlan";
import voyageApi from "@/api/voyages";
import { useRoute } from "vue-router";
import { ReceiptPlanModelV2 } from "@/api/receiptPlan/types";
import { VoyageModel } from "@/api/voyages/types";

/**
 * A store which holds shared state related to the currently active dischargePlan
 */
export const useDischargePlanStore = defineStore("dischargePlan", () => {
  const route = useRoute();

  const activePlan = ref<PlanModelV2>();
  const activePlanId = ref<number | undefined>(
    route.params?.planId ? parseInt(route.params.planId as string) : undefined,
  );
  const receiptPlans = ref<ReceiptPlanModelV2[]>();
  const planVoyage = ref<VoyageModel>();

  // Updates the plan data from the server
  async function RefreshPlanData() {
    if (!activePlanId.value) {
      activePlan.value = undefined;
      return;
    }
    activePlan.value = await dischargePlanApi.getPlanForPlanIdV2(
      activePlanId.value,
    );
    const recieptPlansResponse = await receiptPlanApi.listReceiptPlans(
      activePlanId.value,
    );
    receiptPlans.value = recieptPlansResponse.data;
  }

  async function fetchVoyageData() {
    if (activePlan.value?.voyageId == null) {
      planVoyage.value = undefined;
      return;
    }
    planVoyage.value = await voyageApi.getVoyage(activePlan.value.voyageId);
  }

  // Sets the plan id based on the id in the path
  function setPlanIdFromPath() {
    if (route.params.planId) {
      activePlanId.value = parseInt(route.params.planId as string);
    } else {
      activePlanId.value = undefined;
    }
  }

  async function updatePlan(update: UpdatePlanV2) {
    await dischargePlanApi.updatePlanV2(update);
    await RefreshPlanData();
  }

  async function updateReceiptPlans(plans: ReceiptPlanModelV2[]) {
    if (activePlanId.value == null) {
      throw new Error("Plan ID is not set");
    }
    await receiptPlanApi.updateReceiptPlans(activePlanId.value, plans);
    await RefreshPlanData();
  }

  async function abandonPlan(planId: number) {
    await dischargePlanApi.abandonPlan(planId);
  }

  async function reactivatePlan(planId: number) {
    await dischargePlanApi.reactivatePlan(planId);
  }

  /**
   * A list of all the tank plans that have a receipt planned currently.
   */
  const activeTanks = computed(() =>
    receiptPlans.value?.reduce(
      (tanks, receiptPlan) =>
        tanks.concat(
          receiptPlan.tankPlans.filter(
            (tank) => !!tank.plannedReceipt || !!tank.pipelineAdjustments,
          ),
        ),
      [] as TankPlanModel[],
    ),
  );

  /**
   * Returns a specific tank plan based on it's id.
   * I honestly think it could be cool if this was made into it's own api instead of also using the receipt plan one. But
   * a job for another day.
   *
   * @param tankPlanId The id of the tank plan to fetch.
   * @returns The tank plan with that ID
   */
  function getTankPlan(tankPlanId: number) {
    const foundTanks = activeTanks.value?.filter(
      (tank) => tank.id == tankPlanId,
    );
    if (foundTanks == null || foundTanks.length != 1) {
      return null;
    }
    return foundTanks[0];
  }

  /**
   * Updates a specific tank plan on a specific receipt plan.
   * As with the get I think it would be neat if this was made into it's own api to
   * prevent this annoying fetching and collecting logic.
   *
   * @param receiptPlanId The receipt plan this tank plan belongs to.
   * @param plan The new state of the tank plan.
   */
  async function updateTankPlan(receiptPlanId: number, plan: TankPlanModel) {
    if (!activePlan.value || receiptPlans.value == null) {
      throw new Error("No plan to update");
    }
    if (activePlan.value.id == null) {
      throw new Error("Plan has no id");
    }

    if (plan.id == null) {
      throw new Error("Tank plan has no id");
    }

    // Basically find the receipt plan to be updated and apply that update.
    const planToUpdate = receiptPlans.value?.find(
      (plan) => plan.id == receiptPlanId,
    );
    if (planToUpdate == null) {
      throw new Error("Could not find plan to update");
    }

    const tankPlanIndex = planToUpdate.tankPlans.findIndex(
      (tankPlan) => tankPlan.id == plan.id,
    );

    // This is a little problematic because I am mutating the original state, which means
    // if this fails the UI will look like it works. If you want to fix this, you should instead
    // just make a new API endpoint so we can avoid all this annoying logic.
    planToUpdate.tankPlans[tankPlanIndex] = plan;
    updateReceiptPlans(receiptPlans.value);
    await RefreshPlanData();
  }

  watch(activePlanId, RefreshPlanData);

  // Keep the planId set based on the route
  watch(route, setPlanIdFromPath);

  watch(() => activePlan?.value?.voyageId, fetchVoyageData, {
    immediate: true,
  });

  // Load the plan data on first load
  onMounted(RefreshPlanData);

  return {
    activePlanId,
    activePlan,
    planVoyage,
    getTankPlan,
    updatePlan,
    updateTankPlan,
    updateReceiptPlans,
    abandonPlan,
    reactivatePlan,
    receiptPlans,
    RefreshPlanData,
  };
});
